<template>
  <div class='opc-succezz-thx' :class="{
    'opc-modal': $context.is.modal
  }">
    <div class='opc-succezz-thx__status'>
      <img src='@/assets/success/confirmation_check.svg' alt=''>
    </div>
    <h1 class='opc-succezz-thx__title' :aria-label='zuoraOutageTitle || title || displayTitle'>{{ zuoraOutageTitle || title || displayTitle }}</h1>
    <zm-alert
      v-if="isZuoraOutage"
      type="warning"
      class="opc-payment-cards__zuora-outage-warning"
      show-icon
      :closable="false"
    >{{ $t('billing.zuoraoutage.confirmation_warning')}}</zm-alert>
    <div class='opc-succezz-thx__sub-title'>
      <span ref='psoRef' v-if="oneTimeWebinarTitle" v-html="oneTimeWebinarTitle"></span>
      <span v-else-if="isAPM" v-html="apmSubtitle"></span>
      <span v-else v-html="$t('billing.success.confirmation.receive_a_confirmation_email', [`<b style='font-weight: 600'>${email}</b>`])"></span>
      <slot name='subtitle'></slot>
    </div>
    <div class='opc-succezz-thx__actions' :class="{
      'opc-modal': $context.is.modal
    }">
      <!-- ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE -->
      <template v-if="isZuoraOutage">
        <view-account-status />
        <download-action />
      </template>
      <slot v-else-if='$slots.default'></slot>
      <template v-else>
        <view-account />
      </template>
    </div>
    <div v-if='$slots.reminder' class='opc-succezz-thx__reminder'>
      <slot name='reminder'></slot>
    </div>
    <partner-promote />
  </div>
</template>

<script>
import { APM_PAYMENT_METHODS, BILLING_CYCLE, planTypes } from '@/common/constants';
import { mapGetters } from 'vuex';
import PartnerPromote from '@/views/succezz/components/partner-promote.vue';
import ViewAccount from '@/views/succezz/thanks-template/view-account.vue';
import ViewAccountStatus from '@/views/succezz/thanks-template/view-account-status.vue';
import DownloadAction from '@/views/succezz/thanks-template/download-action.vue';

export default {
  components: {
    ViewAccount,
    PartnerPromote,
    ViewAccountStatus,
    DownloadAction
  },
  props: {
    email: {
      type: String,
      default: window?.SB?.user?.email,
    },
    title: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters(['persistState']),
    isZuoraOutage() {
      // ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE
      return this.persistState.confirmationDetails?.isZuoraOutage && this.persistState.confirmationDetails?.apmName === APM_PAYMENT_METHODS.ALTERNATIVE_CREDIT_CARD;
    },
    apmSubtitle() {
      // ALTERNATIVE CREDIT CARD FOR ZUORA OUTAGE
      if(this.isZuoraOutage) {
        return this.$t('billing.zuoraoutage.success_subtitle', [`<b style='font-weight: 600'>${this.email}</b>`])
      }
      if (this.persistState.confirmationDetails?.apmName === APM_PAYMENT_METHODS.FURIKOMI) {
        return this.$t('billing.bank_transfer.message', [5, `<b style='font-weight: 600'>${this.email}</b>`]);
      }
      return this.$t('billing.success.process_payment_once_verified', [`<b style='font-weight: 600'>${this.email}</b>`]);
    },
    isAPM() {
      return !!this.persistState.confirmationDetails?.isAPM;
    },
    zuoraOutageTitle() {
      return this.isZuoraOutage ? this.$t('billing.zuoraoutage.success_title'): '';
    },
    displayTitle() {
      if (this.$context.is.buyNow || this.$context.is.basic2pro || this.$context.is.buyTrial) {
        return this.title || this.$t('billing.success.confirmation.thank_you_for_choosing_zoom');
      }
      return this.title || this.$t('billing.success.update_success_plan');
    },
    oneTimeWebinarTitle() {
      const getSelectedPlans = this.persistState.confirmationDetails?.getSelectedPlans ?? [];
      if (getSelectedPlans.find(p => p.type === planTypes.WEBINAR && BILLING_CYCLE.ONE_TIME === p.billing_cycle)) {
        return this.$t('billing.single_use_webinar.success_thanks', { email: this.email });
      }
      return '';
    }
  },
  mounted() {
    const psoRef = this.$refs.psoRef;
    const emailEle = psoRef?.querySelector?.('a');
    this.psoEmailTriggerTrackingFn = () => {
      this.$tracking.buyInteraction(`purchase confirmation`, `email address selected`);
    }
    emailEle && emailEle.addEventListener('click', this.psoEmailTriggerTrackingFn);
  },
  beforeDestroy() {
    const psoRef = this.$refs.psoRef;
    const emailEle = psoRef?.querySelector?.('a');
    emailEle && emailEle.removeEventListener('click', this.psoEmailTriggerTrackingFn);
  }
};
</script>

<style lang='scss' scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/_variables.scss';

.opc-payment-cards__zuora-outage-warning {
  background-color: #FEF7D9;
  color: #00053D;
  border-radius: 8px;
  padding: 16px;
  margin: 24px 0;
  line-height: 21px;
  font-size: 14px;
}
.opc-succezz-thx {
  position: relative;

  &.opc-modal {
    text-align: center;

    .opc-succezz-thx__title {
      text-align: center;
      font-family: $happy-display;
      font-size: 29.778px;
      font-style: normal;
      font-weight: 600;
      line-height: 120%; /* 35.733px */
    }
  }

  &__status {
    font-size: 36px;
    color: #95ebb2;
    margin-bottom: 12px;
  }

  &__title {
    font-family: $happy-display;
    font-size: 32px;
    font-weight: 600;
    line-height: 38.4px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  &__sub-title {
    font-family: Almaden Sans;
    font-size: 18px;
    font-weight: 400;
    line-height: 32px;
    margin-top: 6px;
    word-break: break-word;

    span + span {
      margin-left: 4px;
    }
  }

  &__actions {
    margin-top: 24px;
    &.opc-modal {
      @include media(tablet) {
        .zm-button.zm-button--primary.zm-button--large.is-round {
          width: auto;
          min-width: 120px;
          border-radius: 10px;
        }
      }
    }
    .zm-button.zm-button--primary.zm-button--large.is-round  {
      min-width: unset;
      padding: 8px 24px;
      border-radius: 24px;
    }

    .zm-button.zm-button--large.zm-button--plain {
      line-height: 24px;
    }

    .zm-button {
      margin-top: 8px;
    }

    .opc-succezz_book-specialist.zm-button {
      padding: 7px 12px;
    }

    @include media(mobile, tablet) {
      ::v-deep .zm-button {
        width: 100%;
        padding: $mobile-space/2;
        border-radius: $mobile-space;
      }

      ::v-deep .zm-button+.zm-button {
        margin-left: 0px;
        margin-top: $mobile-space/2;
      }
    }
  }

  &__reminder {
    margin-top: 24px;
    font-size: 16px;
    line-height: 24px;
  }
}
</style>
